/**
 * ConfigurationSchematic — clean, schematic side-view of a LogDrive configuration.
 * Style: black canvas, mono labels, red=input, amber=output, white=fixed.
 *
 *   mode:
 *     "sr"     → Speed Reducer (single input shaft → single reduced output)
 *     "mi"     → Motion Inverter (single input → two coaxial counter-rotating outputs)
 *     "sr-mi"  → Speed Reducer + Motion Inverter (cascaded)
 */

const INPUT = "#FF3B30";
const OUTPUT = "#F59E0B";
const FIXED = "rgba(255,255,255,0.85)";
const MUTED = "rgba(255,255,255,0.55)";
const STROKE = "rgba(255,255,255,0.18)";

function Chevron({ x, y, color, dir = "right" }) {
  const d =
    dir === "right"
      ? `M ${x - 4} ${y - 4} L ${x + 2} ${y} L ${x - 4} ${y + 4}`
      : `M ${x + 4} ${y - 4} L ${x - 2} ${y} L ${x + 4} ${y + 4}`;
  return <path d={d} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" />;
}

function Shaft({ x1, x2, y, color, label, dir }) {
  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <Chevron x={dir === "right" ? x2 - 8 : x1 + 8} y={y} color={color} dir={dir} />
      <text
        x={(x1 + x2) / 2}
        y={y - 8}
        fill={color}
        fontFamily="JetBrains Mono, monospace"
        fontSize="9"
        textAnchor="middle"
        letterSpacing="1.5"
      >
        {label}
      </text>
    </g>
  );
}

function DriveBody({ x, y, w, h, label }) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="4" fill="#0a0a0a" stroke={STROKE} strokeWidth="1" />
      {/* mounting flanges */}
      <rect x={x - 3} y={y + 8} width="3" height={h - 16} fill={STROKE} />
      <rect x={x + w} y={y + 8} width="3" height={h - 16} fill={STROKE} />
      {/* modulator ring */}
      <circle cx={cx} cy={cy} r={h / 2 - 8} fill="none" stroke={MUTED} strokeWidth="1" />
      <circle cx={cx} cy={cy} r={h / 2 - 14} fill="none" stroke={MUTED} strokeWidth="0.8" strokeDasharray="2 3" />
      {/* center dot */}
      <circle cx={cx} cy={cy} r="2" fill={MUTED} />
      {label && (
        <text
          x={cx}
          y={y + h + 12}
          fill={MUTED}
          fontFamily="JetBrains Mono, monospace"
          fontSize="8"
          textAnchor="middle"
          letterSpacing="1.5"
        >
          {label}
        </text>
      )}
    </g>
  );
}

export default function ConfigurationSchematic({ mode }) {
  // 300×170 viewBox — shaft centerline at y=85
  const cy = 85;

  let inner = null;

  if (mode === "sr") {
    inner = (
      <>
        <Shaft x1={30} x2={108} y={cy} color={INPUT} label="INPUT" dir="right" />
        <DriveBody x={108} y={cy - 35} w={86} h={70} label="REDUCTION" />
        <Shaft x1={194} x2={272} y={cy} color={OUTPUT} label="OUTPUT" dir="right" />
      </>
    );
  } else if (mode === "mi") {
    // Single input on left, two coaxial outputs on right — second output drawn on
    // a slightly offset coaxial line to convey two-output topology.
    inner = (
      <>
        <Shaft x1={30} x2={108} y={cy} color={INPUT} label="INPUT" dir="right" />
        <DriveBody x={108} y={cy - 35} w={86} h={70} label="INVERTER" />
        {/* Outer output sleeve */}
        <line x1={194} y1={cy - 6} x2={272} y2={cy - 6} stroke={OUTPUT} strokeWidth="2.5" strokeLinecap="round" />
        <Chevron x={264} y={cy - 6} color={OUTPUT} dir="right" />
        {/* Inner counter-rotating output */}
        <line x1={194} y1={cy + 6} x2={272} y2={cy + 6} stroke={OUTPUT} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 2" />
        <Chevron x={264} y={cy + 6} color={OUTPUT} dir="left" />
        <text
          x={233}
          y={cy - 14}
          fill={OUTPUT}
          fontFamily="JetBrains Mono, monospace"
          fontSize="9"
          textAnchor="middle"
          letterSpacing="1.5"
        >
          OUTPUT ↻ ↺
        </text>
      </>
    );
  } else {
    // sr-mi — cascade: SR body then MI body
    inner = (
      <>
        <Shaft x1={20} x2={84} y={cy} color={INPUT} label="INPUT" dir="right" />
        <DriveBody x={84} y={cy - 32} w={64} h={64} label="SR" />
        <line x1={148} y1={cy} x2={156} y2={cy} stroke={STROKE} strokeWidth="2" />
        <DriveBody x={156} y={cy - 32} w={64} h={64} label="MI" />
        <line x1={220} y1={cy - 6} x2={284} y2={cy - 6} stroke={OUTPUT} strokeWidth="2.5" strokeLinecap="round" />
        <Chevron x={276} y={cy - 6} color={OUTPUT} dir="right" />
        <line x1={220} y1={cy + 6} x2={284} y2={cy + 6} stroke={OUTPUT} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 2" />
        <Chevron x={276} y={cy + 6} color={OUTPUT} dir="left" />
        <text
          x={252}
          y={cy - 14}
          fill={OUTPUT}
          fontFamily="JetBrains Mono, monospace"
          fontSize="9"
          textAnchor="middle"
          letterSpacing="1.5"
        >
          OUTPUT ↻ ↺
        </text>
      </>
    );
  }

  return (
    <svg
      viewBox="0 0 300 170"
      width="100%"
      height="auto"
      role="img"
      aria-label={`LogDrive ${mode} schematic`}
    >
      {/* Centerline */}
      <line
        x1={15}
        x2={285}
        y1={cy}
        y2={cy}
        stroke="rgba(255,255,255,0.07)"
        strokeDasharray="2 3"
        strokeWidth="1"
      />
      {inner}
      {/* Mini legend at bottom */}
      <g transform="translate(15 152)">
        <rect width="6" height="6" fill={INPUT} />
        <text
          x={10}
          y={6}
          fill="rgba(255,255,255,0.6)"
          fontFamily="JetBrains Mono, monospace"
          fontSize="8"
          letterSpacing="1"
        >
          INPUT
        </text>
        <rect x={56} width="6" height="6" fill={OUTPUT} />
        <text
          x={66}
          y={6}
          fill="rgba(255,255,255,0.6)"
          fontFamily="JetBrains Mono, monospace"
          fontSize="8"
          letterSpacing="1"
        >
          OUTPUT
        </text>
        <rect x={116} width="6" height="6" fill={FIXED} />
        <text
          x={126}
          y={6}
          fill="rgba(255,255,255,0.6)"
          fontFamily="JetBrains Mono, monospace"
          fontSize="8"
          letterSpacing="1"
        >
          HOUSING
        </text>
      </g>
    </svg>
  );
}
